/**
 * UnitsTabMap
 *
 * Renders the offer's media units on a MapLibre map for the internal
 * (salesperson/planner) units tab. Reuses the shared `MapView` primitive from
 * `@ooh/ui` — the same component the client offer view renders — so pins look
 * consistent across both views.
 *
 * Units without coordinates are omitted from the pin set. When no unit has
 * coordinates, an empty-state message is shown instead of an empty map.
 *
 * Traceability: CU-86ca6grcx — internal offer view map alongside the unit list.
 */
import * as React from 'react';
import { MapPin as MapPinIcon } from 'lucide-react';
import type { TFunction } from 'i18next';
import { Button } from '@ooh/ui/components/button';
import { MapView } from '@ooh/ui/components/map-view';
import type { MapPin } from '@ooh/ui/components/map-view';
import { mediaTypeColors } from '../../../lib/map-config';
import type { OfferUnitItem } from './units-tab-columns';

interface UnitWithCoordinates extends OfferUnitItem {
  latitude: number;
  longitude: number;
}

function hasCoordinates(unit: OfferUnitItem): unit is UnitWithCoordinates {
  return unit.latitude != null && unit.longitude != null;
}

function unitToPin(unit: UnitWithCoordinates): MapPin {
  return {
    id: unit.id,
    latitude: unit.latitude,
    longitude: unit.longitude,
    // Color pins by media type, matching the main Mapa route's pin-coloring pattern
    // (routes/map/use-map-pins-data.ts).
    category: unit.mediaType ?? 'inne',
    properties: { mediaUnitId: unit.mediaUnitId },
  };
}

function averageCenter(units: UnitWithCoordinates[]): [number, number] {
  const lat = units.reduce((sum, u) => sum + u.latitude, 0) / units.length;
  const lng = units.reduce((sum, u) => sum + u.longitude, 0) / units.length;
  return [lat, lng];
}

interface MapPopupProps {
  unit: UnitWithCoordinates;
  onClose: () => void;
  onUnitClick: (mediaUnitId: number) => void;
  t: TFunction<'offers'>;
}

function MapPopup({ unit, onClose, onUnitClick, t }: MapPopupProps) {
  return (
    <div className="min-w-56 max-w-72 text-sm" data-testid={`offer-units-map-popup-${unit.id}`}>
      <p className="font-semibold text-foreground">{unit.city}</p>
      <p className="text-xs text-muted-foreground">{unit.address}</p>
      <dl className="mt-2 space-y-0.5 text-xs">
        {unit.format != null ? (
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{t('workspace.unitsTab.columns.format')}</dt>
            <dd className="text-foreground">{unit.format}</dd>
          </div>
        ) : null}
        {unit.supplier != null ? (
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{t('workspace.unitsTab.columns.supplier')}</dt>
            <dd className="text-foreground">{unit.supplier}</dd>
          </div>
        ) : null}
        {unit.dimensions != null ? (
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{t('workspace.unitsTab.columns.dimensions')}</dt>
            <dd className="text-foreground">{unit.dimensions}</dd>
          </div>
        ) : null}
      </dl>
      <div className="mt-3 flex justify-end">
        <Button
          type="button"
          size="xs"
          variant="outline"
          data-testid={`offer-units-map-popup-details-${unit.id}`}
          onClick={() => {
            onUnitClick(unit.mediaUnitId);
            onClose();
          }}
        >
          {t('workspace.unitsTab.map.popupDetails')}
        </Button>
      </div>
    </div>
  );
}

interface UnitsTabMapProps {
  units: OfferUnitItem[];
  onUnitClick: (mediaUnitId: number) => void;
  t: TFunction<'offers'>;
  className?: string;
}

export function UnitsTabMap({ units, onUnitClick, t, className }: UnitsTabMapProps) {
  const unitsWithCoords = React.useMemo(() => units.filter(hasCoordinates), [units]);
  const pins = React.useMemo(() => unitsWithCoords.map(unitToPin), [unitsWithCoords]);
  const unitByMediaPin = React.useMemo(() => {
    const map = new Map<string, UnitWithCoordinates>();
    for (const unit of unitsWithCoords) map.set(String(unit.id), unit);
    return map;
  }, [unitsWithCoords]);

  const center = React.useMemo(
    () => (unitsWithCoords.length > 0 ? averageCenter(unitsWithCoords) : undefined),
    [unitsWithCoords],
  );

  const renderPopup = React.useCallback(
    (pin: MapPin, onClose: () => void) => {
      const unit = unitByMediaPin.get(String(pin.id));
      if (unit == null) return null;
      return <MapPopup unit={unit} onClose={onClose} onUnitClick={onUnitClick} t={t} />;
    },
    [unitByMediaPin, onUnitClick, t],
  );

  if (pins.length === 0) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center"
        data-testid="offer-units-map-empty"
      >
        <MapPinIcon className="h-8 w-8 text-muted-foreground/50" strokeWidth={1.5} />
        <p className="text-sm text-muted-foreground">{t('workspace.unitsTab.map.empty')}</p>
      </div>
    );
  }

  return (
    <div className={className} data-testid="offer-units-map">
      <MapView
        pins={pins}
        renderPopup={renderPopup}
        categoryColors={mediaTypeColors}
        initialCenter={center}
        initialZoom={6}
        className="h-full w-full"
      />
    </div>
  );
}
